import React, { useEffect, useRef, useState } from 'react';

const StarsBackground = () => {
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!dimensions.width || !dimensions.height) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let stars = [];
    let meteors = [];
    let mouse = { x: 0, y: 0 };
    let lastMeteorTime = 0;
    const meteorInterval = 3000;

    // Set canvas size
    canvas.width = dimensions.width;
    canvas.height = dimensions.height * 0.33;

    // Initialize stars
    const initStars = () => {
      stars = [];
      const starCount = Math.floor(
        (dimensions.width * dimensions.height * 0.33) / 2000
      );

      for (let i = 0; i < starCount; i++) {
        const x = Math.random() * canvas.width;

        let maxY;
        if (x < dimensions.width * 0.03) {
          maxY = dimensions.height * 0.15;
        } else if (x < dimensions.width * 0.1) {
          maxY = dimensions.height * 0.2;
        } else if (x < dimensions.width * 0.2) {
          maxY = dimensions.height * 0.21;
        } else {
          maxY = dimensions.height * 0.4;
        }

        stars.push({
          x: x,
          y: Math.random() * maxY,
          radius: Math.random() * 1.2 + 0.3,
          originalRadius: Math.random() * 1.2 + 0.3,
          opacity: Math.random() * 0.5 + 0.5,
          blinkSpeed: Math.random() * 0.02 + 0.01,
          isFeatureStar: Math.random() > 0.9,
        });
      }
    };

    // Create a new meteor with varied colors
    const createMeteor = (timestamp) => {
      if (timestamp - lastMeteorTime < meteorInterval) return;
      lastMeteorTime = timestamp;

      const startX = Math.random() * canvas.width;
      const startY = -10;
      const length = 50 + Math.random() * 30;
      const speed = 5 + Math.random() * 3;
      const angle =
        Math.PI / 4 + ((Math.random() * Math.PI) / 8 - Math.PI / 16);

      // Meteor color types
      const type = Math.random();
      let color;

      if (type < 0.6) {
        // Common (yellow-white)
        color = `rgba(255, ${Math.floor(
          180 + Math.random() * 75
        )}, ${Math.floor(120 + Math.random() * 80)}, 1)`;
      } else if (type < 0.85) {
        // Metallic (blue-white)
        color = `rgba(${Math.floor(180 + Math.random() * 75)}, ${Math.floor(
          180 + Math.random() * 75
        )}, 255, 1)`;
      } else if (type < 0.95) {
        // Carbon (red-orange)
        color = `rgba(255, ${Math.floor(
          80 + Math.random() * 100
        )}, ${Math.floor(40 + Math.random() * 60)}, 1)`;
      } else {
        // Rare (green)
        color = `rgba(${Math.floor(80 + Math.random() * 60)}, 255, ${Math.floor(
          120 + Math.random() * 80
        )}, 1)`;
      }

      meteors.push({
        x: startX,
        y: startY,
        length: length,
        speed: speed,
        angle: angle,
        opacity: 1,
        decayRate: 0.01 + Math.random() * 0.01,
        color: color,
      });
    };

    const drawMeteor = (meteor, ctx) => {
      const endX = meteor.x - meteor.length * Math.cos(meteor.angle);
      const endY = meteor.y - meteor.length * Math.sin(meteor.angle);

      const gradient = ctx.createLinearGradient(meteor.x, meteor.y, endX, endY);
      gradient.addColorStop(
        0,
        meteor.color.replace('1)', `${meteor.opacity})`)
      );
      gradient.addColorStop(1, meteor.color.replace('1)', '0)'));

      ctx.strokeStyle = gradient;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(meteor.x, meteor.y);
      ctx.lineTo(endX, endY);
      ctx.stroke();

      ctx.fillStyle = meteor.color.replace('1)', `${meteor.opacity})`);
      ctx.beginPath();
      ctx.arc(meteor.x, meteor.y, 2, 0, Math.PI * 2);
      ctx.fill();
    };

    const updateMeteors = () => {
      meteors = meteors.filter((meteor) => {
        meteor.x += meteor.speed * Math.cos(meteor.angle);
        meteor.y += meteor.speed * Math.sin(meteor.angle);
        meteor.opacity -= meteor.decayRate;

        return (
          meteor.y < canvas.height + meteor.length &&
          meteor.x > -meteor.length &&
          meteor.x < canvas.width + meteor.length &&
          meteor.opacity > 0
        );
      });
    };

    const drawStars = (timestamp) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (Math.random() < 0.003) {
        createMeteor(timestamp);
      }

      updateMeteors();
      meteors.forEach((meteor) => drawMeteor(meteor, ctx));

      stars.forEach((star) => {
        if (!star.isFeatureStar) return;

        const dx = star.x - mouse.x;
        const dy = star.y - mouse.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const proximityFactor = distance < 200 ? 1.3 - distance / 200 : 1;

        const glow = star.originalRadius * 3 * proximityFactor;
        const gradient = ctx.createRadialGradient(
          star.x,
          star.y,
          0,
          star.x,
          star.y,
          glow
        );
        gradient.addColorStop(0, `rgba(255, 255, 255, ${star.opacity * 0.7})`);
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(star.x, star.y, glow, 0, Math.PI * 2);
        ctx.fill();
      });

      stars.forEach((star) => {
        const dx = star.x - mouse.x;
        const dy = star.y - mouse.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const proximityFactor = distance < 150 ? 1.5 - distance / 150 : 1;
        const twinkleFactor = Math.sin(timestamp * star.blinkSpeed) * 0.2 + 0.8;

        if (star.isFeatureStar) {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.beginPath();
          ctx.arc(
            star.x,
            star.y,
            star.radius * proximityFactor * 1.2,
            0,
            Math.PI * 2
          );
          ctx.fill();

          ctx.strokeStyle = `rgba(255, 255, 255, ${0.4 * proximityFactor})`;
          ctx.lineWidth = 0.8;
          for (let i = 0; i < 4; i++) {
            const angle = (i * Math.PI) / 2 + timestamp * 0.001;
            const spikeLength = star.radius * 2 * proximityFactor;
            ctx.beginPath();
            ctx.moveTo(star.x, star.y);
            ctx.lineTo(
              star.x + Math.cos(angle) * spikeLength,
              star.y + Math.sin(angle) * spikeLength
            );
            ctx.stroke();
          }
        } else {
          ctx.globalAlpha = star.opacity * twinkleFactor;
          ctx.fillStyle = 'white';
          ctx.beginPath();
          ctx.arc(
            star.x,
            star.y,
            star.radius * proximityFactor,
            0,
            Math.PI * 2
          );
          ctx.fill();
        }
      });

      requestAnimationFrame(drawStars);
    };

    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      if (e.clientX < dimensions.width * 0.1) {
        mouse.y = e.clientY * 0.13;
      } else if (e.clientX < dimensions.width * 0.2) {
        mouse.y = e.clientY * 0.19;
      } else {
        mouse.y = e.clientY * 0.4;
      }
    };

    initStars();
    const animationId = requestAnimationFrame(drawStars);

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [dimensions]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '33%',
        pointerEvents: 'none',
        zIndex: 1,
        mixBlendMode: 'screen',
        maskImage: 'linear-gradient(to bottom, white 70%, transparent 90%)',
        WebkitMaskImage:
          'linear-gradient(to bottom, white 70%, transparent 90%)',
      }}
    />
  );
};

export default StarsBackground;
